import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/context/AuthContext";
import { formatUtcTimestampNz } from "@/lib/nzDate";

export default function InstructorAdvancedAccess({ learnerId, programmeId, assignments, onChanged }) {
  const [grants, setGrants] = useState([]);
  const [catalogue, setCatalogue] = useState([]);
  const [eligibilityRecorded, setEligibilityRecorded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [selectedProgramme, setSelectedProgramme] = useState(programmeId || "");

  const allocatedIds = new Set(
    (assignments || []).filter((a) => a.learner_id === learnerId).map((a) => a.programme_id)
  );

  const load = useCallback(() => {
    if (!learnerId) return Promise.resolve();
    return Promise.all([
      api
        .get("/access-workflow/instructor/advanced-access", { params: { learner_id: learnerId } })
        .then((res) => {
          setGrants(res.data.grants || []);
          setEligibilityRecorded(Boolean(res.data.eligibility_recorded));
        })
        .catch(() => setGrants([])),
      api
        .get("/access-workflow/instructor/programme-allocations/catalogue", { params: { learner_id: learnerId } })
        .then((res) => setCatalogue(res.data.programmes || []))
        .catch(() => setCatalogue([])),
    ]);
  }, [learnerId]);

  useEffect(() => {
    const fromAssign = (assignments || []).filter((a) => a.learner_id === learnerId).map((a) => a.programme_id);
    const fromCat = catalogue.map((p) => p.id);
    const options = [...new Set([...fromAssign, ...fromCat, programmeId].filter(Boolean))];
    if (!selectedProgramme && options[0]) setSelectedProgramme(options[0]);
  }, [learnerId, programmeId, assignments, catalogue, selectedProgramme]);

  useEffect(() => {
    load();
  }, [load]);

  const grantForProgramme = grants.find(
    (g) => g.programme_id === selectedProgramme && (g.status === "active" || g.status === "on_hold")
  );
  const programmeOptions = [
    ...new Set([
      ...(catalogue.map((p) => p.id) || []),
      ...allocatedIds,
      programmeId,
    ].filter(Boolean)),
  ];

  async function allocateProgramme() {
    if (!selectedProgramme) return;
    setBusy(true);
    setError("");
    try {
      await api.post("/access-workflow/instructor/programme-allocations", {
        learner_id: learnerId,
        programme_id: selectedProgramme,
      });
      setMessage("Programme allocated (not activated).");
      onChanged?.();
      await load();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }

  async function activate() {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      await api.post("/access-workflow/instructor/advanced-access/activate", {
        learner_id: learnerId,
        programme_id: selectedProgramme,
      });
      setMessage("Advanced pathway activated.");
      await load();
      onChanged?.();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }

  async function holdOrResume(action) {
    if (!grantForProgramme) return;
    setBusy(true);
    setError("");
    try {
      await api.post(`/access-workflow/instructor/advanced-access/${grantForProgramme.id}/${action}`, { reason: "" });
      await load();
      onChanged?.();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }

  async function deactivate() {
    if (!grantForProgramme) return;
    setBusy(true);
    setError("");
    try {
      await api.post(`/access-workflow/instructor/advanced-access/${grantForProgramme.id}/deactivate`, {
        reason: "Instructor deactivated pathway",
      });
      setMessage("Advanced access deactivated for this pathway.");
      await load();
      onChanged?.();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }

  if (!learnerId) return null;

  return (
    <section className="card space-y-3" data-testid="instructor-advanced-access">
      <h3 className="font-semibold text-sm">Programme allocation &amp; advanced access</h3>
      <p className="text-xs text-muted-foreground">
        Allocate any published programme for this learner (not limited to signup interest). Activation still requires
        eligibility — selecting a programme does not waive it.
      </p>
      <label className="block text-sm">
        Programme
        <select
          className="input mt-1"
          value={selectedProgramme}
          onChange={(e) => setSelectedProgramme(e.target.value)}
          data-testid="advanced-access-programme-select"
        >
          {programmeOptions.map((pid) => (
            <option key={pid} value={pid}>{pid}</option>
          ))}
        </select>
      </label>
      {!allocatedIds.has(selectedProgramme) && selectedProgramme && (
        <button type="button" className="btn-outline" disabled={busy} onClick={allocateProgramme} data-testid="advanced-access-allocate">
          Allocate programme
        </button>
      )}
      <p className="text-sm" data-testid="advanced-access-eligibility-flag">
        Eligibility recorded: {eligibilityRecorded ? "Yes" : "No — contact Admin"}
      </p>
      {grantForProgramme && (
        <p className="text-sm" data-testid="advanced-access-grant-status">
          Status: {grantForProgramme.status}
          {grantForProgramme.expires_at ? ` · expires ${formatUtcTimestampNz(grantForProgramme.expires_at)}` : ""}
        </p>
      )}
      {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
      {message && <p className="text-sm text-green-700">{message}</p>}
      <div className="flex flex-wrap gap-2">
        {!grantForProgramme && (
          <button
            type="button"
            className="btn-accent"
            disabled={busy || !eligibilityRecorded || !allocatedIds.has(selectedProgramme)}
            onClick={activate}
            data-testid="advanced-access-activate"
          >
            Activate pathway
          </button>
        )}
        {grantForProgramme?.status === "active" && (
          <button type="button" className="btn-outline" disabled={busy} onClick={() => holdOrResume("hold")} data-testid="advanced-access-hold">
            Hold access
          </button>
        )}
        {grantForProgramme?.status === "on_hold" && (
          <button type="button" className="btn-outline" disabled={busy} onClick={() => holdOrResume("resume")} data-testid="advanced-access-resume">
            Resume access
          </button>
        )}
        {grantForProgramme && (
          <button type="button" className="btn-outline" disabled={busy} onClick={deactivate} data-testid="advanced-access-deactivate">
            Deactivate
          </button>
        )}
      </div>
    </section>
  );
}
